import { CommonModule, CurrencyPipe, DecimalPipe } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { finalize, takeUntil } from 'rxjs';
import {
  AiSearchFilters,
  AiSearchMode,
  AiSearchResponse,
  AiSearchResult,
} from '../../models/ai-search';
import { AiSearchService } from '../../services/ai-search.service';
import { MenuComponentBase } from '../../../../shared/base-classes/menu-component-base';
import { RecordDetailsComponent } from '../../../../shared/components/record-details/record-details.component';
import { AuthService } from '../../../auth/services/auth.service';
import { PermissionCode } from '../../../../shared/enums/permission-code';

interface ExampleQuery {
  label: string;
  query: string;
  mode: AiSearchMode;
  top: number;
  filters?: AiSearchFilters;
}

@Component({
  selector: 'app-ai-search-lab',
  standalone: true,
  imports: [
    CommonModule,
    CurrencyPipe,
    DecimalPipe,
    ReactiveFormsModule,
    RecordDetailsComponent,
  ],
  templateUrl: './ai-search-lab.component.html',
  styleUrl: './ai-search-lab.component.scss',
})
export class AiSearchLabComponent extends MenuComponentBase implements OnInit {
  private fb = inject(FormBuilder);
  private aiSearchService = inject(AiSearchService);
  private router = inject(Router);
  private authService = inject(AuthService);
  private presetOnlyFilters: Partial<AiSearchFilters> = {};

  readonly modes: { label: string; value: AiSearchMode }[] = [
    { label: 'Hybrid', value: 'hybrid' },
    { label: 'Vector', value: 'vector' },
    { label: 'Keyword', value: 'keyword' },
    { label: 'Semantic hybrid', value: 'semantic_hybrid' },
  ];

  readonly states = [
    'North Carolina',
    'Virginia',
    'Tennessee',
    'Georgia',
    'West Virginia',
  ];

  readonly productOptions = [
    { label: 'Loans and capital', value: 'loans_and_capital' },
    { label: 'Technical assistance', value: 'technical_assistance' },
    { label: 'Solar and energy', value: 'solar_and_energy' },
    { label: 'Commercial real estate', value: 'commercial_real_estate' },
    { label: 'Finding investors', value: 'finding_investors' },
    { label: 'Business tools', value: 'business_operational_tools' },
  ];

  readonly examples: ExampleQuery[] = [
    {
      label: 'Solar financing leads',
      query:
        'Find businesses looking for solar financing or solar energy upgrades',
      mode: 'hybrid',
      top: 10,
    },
    {
      label: 'Capital + TA',
      query: 'Find businesses that need both capital and technical assistance',
      mode: 'hybrid',
      top: 10,
    },
    {
      label: 'Startup funding prospects',
      query: 'Show startup or planning-stage businesses that need funding',
      mode: 'hybrid',
      top: 10,
      filters: { has_business: 'planning' },
    },
    {
      label: 'Larger loan requests',
      query: 'Find strong loan opportunities with larger financing needs',
      mode: 'hybrid',
      top: 10,
      filters: { loan_amount_min: 100000 },
    },
    {
      label: 'Commercial real estate',
      query:
        'Find businesses looking to buy rent or upgrade commercial real estate',
      mode: 'hybrid',
      top: 10,
      filters: { interested_products: ['commercial_real_estate'] },
    },
    {
      label: 'Equipment financing',
      query:
        'Find businesses that need equipment financing facility upgrades or project finance',
      mode: 'hybrid',
      top: 10,
    },
    {
      label: 'North Carolina opportunities',
      query:
        'Find promising businesses needing financing or technical assistance',
      mode: 'hybrid',
      top: 10,
      filters: { state: 'North Carolina' },
    },
    {
      label: 'Strong credit borrowers',
      query:
        'Find applicants with strong credit who are seeking business financing',
      mode: 'hybrid',
      top: 10,
      filters: { credit_score_range: '740+' },
    },
    {
      label: 'Marketing assistance',
      query:
        'Find businesses that need help with marketing strategy website design or customer acquisition',
      mode: 'hybrid',
      top: 10,
    },
    {
      label: 'Semantic hybrid comparison',
      query:
        'Which businesses look like the best fit for a community lender focused on clean energy and working capital?',
      mode: 'semantic_hybrid',
      top: 10,
    },
  ];

  searchForm = this.fb.nonNullable.group({
    query: [
      'Find businesses that need both capital and technical assistance',
      [Validators.required, Validators.minLength(3)],
    ],
    mode: ['semantic_hybrid' as AiSearchMode, Validators.required],
    top: [10, [Validators.required, Validators.min(1), Validators.max(50)]],
    state: [''],
    loanAmountMin: [null as number | null],
    loanAmountMax: [null as number | null],
    hasBusiness: [''],
    interestedInSolar: [''],
  });

  selectedProducts = new Set<string>();
  response: AiSearchResponse | null = null;
  loading = false;
  errorMessage = '';

  uniqueId: string | null = null;
  showRecordDetails = false;

  viewRecordDetails(uniqueId: string): void {
    this.uniqueId = uniqueId;
    this.showRecordDetails = true;
  }

  override ngOnInit(): void {
    if (!this.authService.hasPermission(PermissionCode.ADMIN_PRIVILEGES)) {
      this.router.navigate(['/dashboard']);
      return;
    }
    super.ngOnInit();
  }

  search(): void {
    if (this.searchForm.invalid || this.loading) {
      this.searchForm.markAllAsTouched();
      return;
    }

    this.loading = true;
    this.errorMessage = '';
    const raw = this.searchForm.getRawValue();

    this.aiSearchService
      .search({
        query: raw.query.trim(),
        mode: raw.mode,
        top: Number(raw.top),
        filters: this.buildFilters(),
      })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.loading = false)),
      )
      .subscribe({
        next: (response) => {
          this.response = response;
        },
        error: (error) => {
          this.errorMessage =
            error?.error?.detail ||
            error?.error?.message ||
            'Search failed. Check the API logs and try again.';
        },
      });
  }

  applyExample(example: ExampleQuery): void {
    this.presetOnlyFilters = {
      credit_score_range: example.filters?.credit_score_range,
    };

    this.searchForm.patchValue({
      query: example.query,
      mode: example.mode,
      top: example.top,
      state: example.filters?.state || '',
      loanAmountMin: example.filters?.loan_amount_min || null,
      loanAmountMax: example.filters?.loan_amount_max || null,
      hasBusiness: example.filters?.has_business || '',
      interestedInSolar:
        example.filters?.interested_in_solar === true ? 'true' : '',
    });
    this.selectedProducts = new Set(example.filters?.interested_products || []);
    this.search();
  }

  toggleProduct(value: string): void {
    if (this.selectedProducts.has(value)) {
      this.selectedProducts.delete(value);
    } else {
      this.selectedProducts.add(value);
    }
  }

  clearFilters(): void {
    this.presetOnlyFilters = {};
    this.searchForm.patchValue({
      state: '',
      loanAmountMin: null,
      loanAmountMax: null,
      hasBusiness: '',
      interestedInSolar: '',
    });
    this.selectedProducts.clear();
  }

  getSnippet(result: AiSearchResult): string {
    const text = result.document.search_text || '';
    return text.length > 340 ? `${text.slice(0, 340)}...` : text;
  }

  getLocation(result: AiSearchResult): string {
    const doc = result.document;
    return [doc.city, doc.county, doc.state].filter(Boolean).join(', ');
  }

  private buildFilters(): AiSearchFilters {
    const raw = this.searchForm.getRawValue();
    const filters: AiSearchFilters = {};

    if (raw.state) {
      filters.state = raw.state;
    }
    if (raw.loanAmountMin !== null) {
      filters.loan_amount_min = Number(raw.loanAmountMin);
    }
    if (raw.loanAmountMax !== null) {
      filters.loan_amount_max = Number(raw.loanAmountMax);
    }
    if (raw.hasBusiness) {
      filters.has_business = raw.hasBusiness;
    }
    if (raw.interestedInSolar) {
      filters.interested_in_solar = raw.interestedInSolar === 'true';
    }
    if (this.selectedProducts.size) {
      filters.interested_products = Array.from(this.selectedProducts);
    }
    if (this.presetOnlyFilters.credit_score_range) {
      filters.credit_score_range = this.presetOnlyFilters.credit_score_range;
    }

    return filters;
  }
}
